import { useEffect, useRef, useState } from "react";
import { Search, Check, Plus, WandSparkles } from "lucide-react";
import client from "../lib/api";

/**
 * DirectoryCombobox
 * -----------------
 * A debounced autocomplete backed by /api/directory/{institutions,programmes}.
 * Shows suggestions as you type, a "did you mean" pill when the top
 * match is close but not exact, and a "Not in the list, add it"
 * fallback that submits the entry to the pending queue via
 * /api/directory/suggest/{institution,programme}.
 *
 * Props:
 *   kind:       "institution" | "programme"
 *   value:      current string value (controlled)
 *   onChange:   (nextValue, meta) => void
 *                 meta is optionally { id, source: "public"|"suggested" }
 *   country:    optional 2-letter country filter (institutions only)
 *   placeholder
 *   testid:     data-testid prefix
 */
export default function DirectoryCombobox({
  kind = "institution",
  value = "",
  onChange,
  country = null,
  placeholder = "start typing.",
  testid = "combo",
}) {
  const [text, setText] = useState(value || "");
  const [results, setResults] = useState([]);
  const [didYouMean, setDidYouMean] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggested, setSuggested] = useState(false);
  const [addCountry, setAddCountry] = useState("IN");
  const debRef = useRef(null);
  const skipNextFetch = useRef(false);

  useEffect(() => { setText(value || ""); }, [value]);

  useEffect(() => {
    // Debounced search on every text change.
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }
    clearTimeout(debRef.current);
    if (!text || text.length < 2) {
      setResults([]); setDidYouMean(null); return;
    }
    debRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const url = kind === "programme"
          ? `/directory/programmes?q=${encodeURIComponent(text)}&limit=8`
          : `/directory/institutions?q=${encodeURIComponent(text)}${country ? `&country=${country}` : ""}&limit=8`;
        const { data } = await client.get(url);
        setResults(data.results || []);
        setDidYouMean(data.did_you_mean || null);
        setOpen(true);
      } catch (_e) {
        setResults([]); setDidYouMean(null);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => clearTimeout(debRef.current);
  }, [text, kind, country]);

  const pick = (name, meta) => {
    skipNextFetch.current = true;
    setText(name);
    setOpen(false);
    setDidYouMean(null);
    onChange?.(name, { source: "public", ...meta });
  };

  const suggestNew = async () => {
    if (!text || suggesting || suggested) return;
    setSuggesting(true);
    try {
      const url = kind === "programme"
        ? "/directory/suggest/programme"
        : "/directory/suggest/institution";
      const body = kind === "programme"
        ? { name: text.trim(), level: null }
        : { name: text.trim(), country: addCountry };
      const r = await client.post(url, body);
      setSuggested(true);
      onChange?.(text.trim(), { source: "suggested", id: r?.data?.id });
      // Small delay so the pill is visible before closing.
      setTimeout(() => setOpen(false), 900);
    } catch (_e) {
      // ignore
    } finally {
      setSuggesting(false);
    }
  };

  const exactMatch = results.some((r) => (r.name || "").toLowerCase() === text.trim().toLowerCase());
  const showAddNew = text.trim().length >= 2 && !exactMatch && !suggested;

  return (
    <div className="relative">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "hsl(var(--klareet-muted))" }} />
        <input
          className="k-input"
          style={{ paddingLeft: "2rem" }}
          value={text}
          onChange={(e) => {
            setSuggested(false);
            const v = e.target.value;
            setText(v);
            onChange?.(v, { source: null });
          }}
          onFocus={() => text.length >= 2 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 140)}
          placeholder={placeholder}
          data-testid={testid}
        />
      </div>

      {didYouMean && didYouMean.name.toLowerCase() !== text.trim().toLowerCase() && (
        <button
          type="button"
          className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium"
          style={{
            background: "hsl(var(--klareet-blue-soft))",
            color: "hsl(var(--klareet-blue-strong))",
            border: "1px solid hsl(var(--klareet-blue))",
          }}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => pick(didYouMean.name, { id: didYouMean.id })}
          data-testid={`${testid}-dym`}
        >
          <WandSparkles size={12} /> Did you mean <span className="font-semibold">{didYouMean.name}</span>?
        </button>
      )}

      {open && (results.length > 0 || showAddNew) && (
        <div
          className="absolute z-40 left-0 right-0 mt-1 rounded-xl border shadow-lg overflow-hidden"
          style={{
            background: "hsl(var(--card))",
            borderColor: "hsl(var(--klareet-line))",
            maxHeight: "20rem",
            overflowY: "auto",
          }}
          data-testid={`${testid}-dropdown`}
        >
          {loading && (
            <div className="px-3 py-2 klareet-mono !text-xs" style={{ color: "hsl(var(--klareet-muted))" }}>
              searching.
            </div>
          )}
          {results.map((r) => (
            <button
              key={r.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(r.name, { id: r.id })}
              className="w-full text-left px-3 py-2 flex items-start gap-2 transition-colors"
              style={{ borderBottom: "1px solid hsl(var(--klareet-line-soft))" }}
              data-testid={`${testid}-opt-${r.id}`}
              onMouseEnter={(e) => { e.currentTarget.style.background = "hsl(var(--klareet-blue-soft))"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <Check size={12} className="mt-1 shrink-0" style={{ color: "hsl(var(--klareet-blue))", opacity: 0.6 }} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate" style={{ color: "hsl(var(--klareet-ink))" }}>{r.name}</div>
                {kind === "institution" ? (
                  <div className="k-quiet !text-xs truncate">
                    {[r.state, r.district].filter(Boolean).join(" · ")}
                    {r.country && r.country !== "IN" ? ` · ${r.country}` : ""}
                  </div>
                ) : (
                  r.level && <div className="k-quiet !text-xs truncate">{r.level}</div>
                )}
              </div>
              {typeof r.score === "number" && (
                <span className="klareet-mono !text-[10px]" style={{ color: "hsl(var(--klareet-muted))" }}>
                  {Math.round(r.score)}
                </span>
              )}
            </button>
          ))}
          {showAddNew && (
            <div
              className="px-3 py-3"
              style={{ background: "hsl(var(--klareet-canvas-2))", color: "hsl(var(--klareet-ink))" }}
              data-testid={`${testid}-addnew-panel`}
            >
              <div className="flex items-start gap-2">
                <Plus size={12} className="mt-1 shrink-0" style={{ color: "hsl(var(--klareet-blue-strong))" }} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium">
                    {suggesting ? "Adding for review." : suggested ? "Sent for review" : `Not in the list. Add "${text.trim()}"`}
                  </div>
                  {kind === "institution" && !suggested && (
                    <div className="flex items-center gap-2 mt-2 flex-wrap" data-testid={`${testid}-addcountry`}>
                      <span className="k-quiet !text-xs">Where is it?</span>
                      {[{ code: "IN", label: "India" }, { code: "AE", label: "UAE" }].map((c) => (
                        <button
                          key={c.code}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => setAddCountry(c.code)}
                          className="k-chip k-chip-outline"
                          style={{
                            background: addCountry === c.code ? "hsl(var(--klareet-ink))" : "transparent",
                            color: addCountry === c.code ? "white" : "hsl(var(--klareet-ink-soft))",
                            borderColor: addCountry === c.code ? "hsl(var(--klareet-ink))" : "hsl(var(--klareet-line))",
                            cursor: "pointer",
                          }}
                          aria-pressed={addCountry === c.code}
                          data-testid={`${testid}-addcountry-${c.code}`}
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="k-quiet !text-xs mt-2">
                    {suggested
                      ? "A super admin will approve it soon. You can keep going with your answer."
                      : "It stays private to you until a super admin approves it."}
                  </div>
                </div>
                {!suggested && (
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={suggestNew}
                    disabled={suggesting}
                    className="k-btn k-btn-primary !py-1.5 !px-3 text-xs shrink-0"
                    data-testid={`${testid}-addnew`}
                  >
                    {suggesting ? "Adding." : "Add it"}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
